import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { getFile } from '#src/app/shared/hooks/file';
import type { ShareAsset, ShareAssetSelection } from '#src/app/shared/hooks/share';
import { getNavigatorShareInterface, isShareCancellation } from '#src/app/shared/share/utils';
import { copyToClipboard } from '#src/app/shared/utils/clipboard';

import { trackShare, trackShareFailure } from './track';

type OpenStoriesEducationModalResult = {
	waitForResult: () => Promise<{ successfulShare: boolean } | undefined>;
};

type Options = {
	trackingData: Record<string, string | number | boolean | undefined>;
	trackingEvents: Record<string, string>;
	/**
	 * Present only for the multiple-vertical-share-assets treatment arms (CHANGE-95456). The assets are
	 * fetched before the modal opens — never lazily on Continue — so every arm reaches
	 * `navigator.share()` with its chosen asset already in hand.
	 */
	picker?: {
		loadAssets: () => Promise<readonly ShareAsset[]>;
		/** Every variation the picker meant to offer — see `ShareAssetPicker`. */
		offeredVariations: readonly string[];
		onAssetSelected: (selection: ShareAssetSelection) => void;
	};
	openStoriesEducationModal: (options: {
		onContinueAction: (selectedIndex: number, hasInteracted: boolean) => Promise<void>;
		shareVisualPreview: File | string | null;
		shareVisualPreviews?: readonly ShareAsset[];
		onAssetSelected?: (selection: ShareAssetSelection) => void;
		onActiveAssetChange?: (selectedIndex: number) => void;
	}) => OpenStoriesEducationModalResult;
};

/**
 * Empty without a picker, so every other Instagram Stories surface keeps the payload it has today —
 * these properties only mean anything for an arm that had a carousel.
 *
 * `hasInteracted` comes from the picker rather than the final position: a supporter who swipes away and
 * back lands on 0 again, and calling that "never interacted" is the conflation this exists to prevent.
 */
function assetTrackingData(pickerAssets: readonly ShareAsset[] | null, selectedIndex: number, hasInteracted: boolean) {
	if (!pickerAssets) return {};

	return {
		share_asset_variation: pickerAssets[selectedIndex]?.variationName,
		share_asset_position: selectedIndex,
		share_asset_total: pickerAssets.length,
		share_asset_interacted: hasInteracted,
	};
}

/**
 * Caught rather than left to propagate: this sits above the intent event, outside `successfulShare`'s try,
 * so an implementation that throws from `canShare` would escape `shareInstagramStories` entirely and take
 * the intent event with it — silently costing the primary metric a denominator row. Throwing and returning
 * false mean the same thing to a supporter, no file share, so both land on the same path.
 *
 * The method itself is guaranteed present: `getNavigatorShareInterface` returns undefined without it, and
 * the caller has already returned by then. This guards a throwing implementation, not a missing one.
 */
function canShareFiles(
	navigator: NonNullable<ReturnType<typeof getNavigatorShareInterface>>,
	payload: { files: File[] },
): boolean {
	try {
		return navigator.canShare(payload);
	} catch {
		return false;
	}
}

/**
 * On the intent event because that is the primary metric's denominator: a supporter who was enrolled but
 * never shown a picker can only be filtered out of the readout if the signal rides on the event being
 * counted.
 *
 * Absence covers three different cases — control, fake_control, and a treated supporter whose petition
 * failed the pickability check — so separate them by arm; the absence alone does not.
 *
 * A device that cannot share files is absent too, for the same reason `assetTrackingData` waits: it never
 * renders a carousel, so describing one would file it alongside supporters who saw the picker.
 */
function pickerTrackingData(
	picker: Options['picker'],
	pickerAssets: readonly ShareAsset[] | null,
	deviceCanShareFiles: boolean,
) {
	if (!picker || !deviceCanShareFiles) return {};

	return {
		share_asset_offered_variations: picker.offeredVariations,
		share_asset_rendered_variations: (pickerAssets ?? []).map(({ variationName }) => variationName),
	};
}

export async function shareInstagramStories(
	data: { url: string; image: File | string | null },
	{ trackingEvents, trackingData, picker, openStoriesEducationModal }: Options,
	track: TrackingFn,
	utilityContext: UtilityContext,
): Promise<boolean> {
	const { url } = data;

	const loadedAssets = picker ? await picker.loadAssets() : null;
	// Two is the minimum that can render a carousel - the modal falls back to the single-preview layout
	// below that - so a partial load leaving one asset is not a picker, and must not report picker
	// properties for a carousel the supporter was never shown. An empty set is the same case: falling back
	// to control's single image keeps the arms from differing on *whether the share works at all*, and
	// routes the failure through `toFile`'s error report rather than dying silently at the guard below.
	const pickerAssets = loadedAssets && loadedAssets.length > 1 ? loadedAssets : null;
	// Asset 0 stands in for the whole set: it is what canShare inspects, what the guard below keys on, and
	// what ships when the supporter never moves the carousel. Read from the loaded set rather than the
	// picker, so a lone survivor - control's own asset, already converted - ships as-is instead of being
	// fetched a second time through toFile.
	const image = loadedAssets?.[0]?.file ?? (await toFile(data.image, utilityContext));

	const navigator = getNavigatorShareInterface();

	if (!image || !navigator) {
		trackShare(trackingEvents.cancel, trackingData, track);
		return false;
	}

	const sharePayload = { files: [image] };

	// Retained so the continue/cancel events below carry the same asset properties. `modal_continue` is the
	// dependable denominator here — the success event under-reports IG shares.
	//
	// Empty until the modal is about to open: a device that fails the canShare guard below never renders a
	// carousel, and describing one anyway files those sessions alongside supporters who saw the picker and
	// ignored it — the cohort ST-6 splits on. Matches the `!image || !navigator` guard above.
	let assetProperties: ReturnType<typeof assetTrackingData> = {};

	const triggerNativeShare = async (selectedIndex = 0, hasInteracted = false) => {
		// Already-fetched, so this is a lookup rather than a fetch: nothing async runs before
		// navigator.share() and the Continue tap's user activation is never at risk.
		const selectedAsset = pickerAssets?.[selectedIndex];
		const selected = selectedAsset?.file ?? image;
		assetProperties = assetTrackingData(pickerAssets, selectedIndex, hasInteracted);
		const shareTrackingData = { ...trackingData, ...assetProperties };

		try {
			/**
			 * Doing a trigger and forget copy to clipboard to avoid being
			 * hit by a transient user gesture error.
			 */
			void copyToClipboard(url, utilityContext.i18n);

			await navigator.share({ files: [selected] });

			trackShare(trackingEvents.success, shareTrackingData, track);
		} catch (e) {
			const error = e as Error;

			// expected user cancellation, not a genuine failure — see isShareCancellation()
			if (isShareCancellation(error)) {
				return;
			}

			void utilityContext.errorReporter.report(new Error('Could not share to instagramStories', { cause: error }));

			trackShareFailure(
				trackingEvents.failure,
				{
					...shareTrackingData,
					errorName: error.name,
					errorMessage: error.message,
				},
				track,
			);
		}
	};

	// Evaluated once, above the intent event, because the picker properties depend on it. A device that
	// fails this never opens the modal.
	const deviceCanShareFiles = canShareFiles(navigator, sharePayload);

	trackShare(
		trackingEvents.intent,
		{ ...trackingData, ...pickerTrackingData(picker, pickerAssets, deviceCanShareFiles) },
		track,
	);

	const successfulShare = await (async () => {
		try {
			if (!deviceCanShareFiles) {
				return false;
			}
			// The carousel is about to render, so an untouched one now legitimately reports position 0.
			assetProperties = assetTrackingData(pickerAssets, 0, false);
			const modalResult = await openStoriesEducationModal({
				onContinueAction: triggerNativeShare,
				shareVisualPreview: image,
				shareVisualPreviews: pickerAssets ?? undefined,
				// Wrapped rather than forwarded: `assetProperties` is otherwise only written on Continue, so
				// closing the modal after browsing reported a cancel at position 0 with interacted false.
				onAssetSelected: picker
					? (selection) => {
							assetProperties = assetTrackingData(pickerAssets, selection.position, true);
							picker.onAssetSelected(selection);
						}
					: undefined,
				// Separate from the callback above because that one is debounced by 80ms, while closing the
				// modal resolves this promise immediately and only *starts* the closing animation - so the
				// cancel event below fires before any settle timer, and read a position the supporter had
				// already scrolled away from. Measured on device: close carrying position 0 / interacted
				// false while the carousel sat on asset 2.
				onActiveAssetChange: picker
					? (selectedIndex) => {
							assetProperties = assetTrackingData(pickerAssets, selectedIndex, true);
						}
					: undefined,
			}).waitForResult();

			return !!modalResult?.successfulShare;
		} catch (error) {
			return false;
		}
	})();

	trackShare(
		successfulShare ? trackingEvents.continue : trackingEvents.cancel,
		{ ...trackingData, ...assetProperties },
		track,
	);

	return successfulShare;
}

async function toFile(file: File | string | null, utilityContext: UtilityContext): Promise<File | null> {
	if (file instanceof File) {
		return file;
	}
	if (typeof file === 'string') {
		/**
		 * An issue was reported that happens for Android where, if we do not include the file `type` when
		 * triggering the native share, the flow breaks and throws an error.
		 * Adding the `type` by default to avoid this issue across the board.
		 *
		 * @see https://change.atlassian.net/browse/CHANGE-92124
		 */
		const res = await getFile(file, undefined, true);

		if (!res) {
			void utilityContext.errorReporter.report(
				new Error(`Failed to get image file from URL for instagramStories share: ${file}`),
			);
		}

		return res;
	}
	void utilityContext.errorReporter.report(new Error('Missing image for instagramStories share'));
	return null;
}
